import { Box, Container, Heading, SimpleGrid, Text, Icon, VStack, Image } from '@chakra-ui/react';
import { motion } from 'framer-motion';
import { TestTube2, GitPullRequest } from 'lucide-react';
import { useInView } from 'react-intersection-observer';
import CTAButton from './CTAButton';
import { useAnalytics } from '../hooks/useAnalytics';

const MotionBox = motion(Box);

const ProductCard = ({
  title,
  description,
  icon,
  status,
  imageSrc,
  onLearnMore,
}: {
  title: string;
  description: string;
  icon: React.ElementType;
  status: string;
  imageSrc?: string;
  onLearnMore?: () => void;
}) => {
  const { ref, inView } = useInView({
    triggerOnce: true,
    threshold: 0.1,
  });
  const { trackEvent } = useAnalytics();

  return (
    <MotionBox
      ref={ref}
      initial={{ opacity: 0, y: 20 }}
      animate={inView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.6 }}
      bg='rgba(11, 61, 145, 0.3)'
      p={{ base: 6, md: 8 }}
      borderRadius='xl'
      border='1px solid'
      borderColor='brand.accent'
      sx={{
        transition: 'all 0.3s',
        '&:hover': {
          transform: 'translateY(-5px)',
          boxShadow: '0 4px 20px rgba(70, 255, 255, 0.2)',
        },
      }}
      className='cosmic-card'
      onMouseEnter={() =>
        trackEvent('product_card_viewed', {
          product: title,
          status: status,
        })
      }>
      <VStack spacing={4} align='flex-start'>
        {imageSrc ? (
          <Box width='100%' mb={2}>
            <Image
              src={imageSrc}
              alt={title}
              borderRadius='lg'
              width='100%'
              height='auto'
              objectFit='cover'
            />
          </Box>
        ) : (
          <Icon as={icon} boxSize={{ base: 8, md: 10 }} color='brand.accent' />
        )}
        <Heading size={{ base: 'md', md: 'lg' }} color='brand.light'>
          {title}
        </Heading>
        <Text color='gray.300' fontSize={{ base: 'sm', md: 'md' }}>
          {description}
        </Text>
        <Box
          px={3}
          py={1}
          borderRadius='full'
          bg='rgba(70, 255, 255, 0.1)'
          border='1px solid'
          borderColor='brand.accent'>
          <Text fontSize={{ base: 'xs', md: 'sm' }} color='brand.accent'>
            {status}
          </Text>
        </Box>
        <CTAButton
          text='Join Beta'
          size='md'
          onClick={onLearnMore}
          width={{ base: '100%', md: 'auto' }}
        />
      </VStack>
    </MotionBox>
  );
};

const Products = () => {
  const { trackEvent } = useAnalytics();

  const handleProductClick = (productName: string) => {
    trackEvent('product_interest', {
      product: productName,
    });

    const element = document.getElementById('contact');
    element?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <Box
      id='products'
      py={{ base: 12, md: 20 }}
      bg='brand.dark'
      onMouseEnter={() => trackEvent('section_viewed', { section: 'products' })}>
      <Container maxW='1200px' px={{ base: 4, md: 6 }}>
        <VStack spacing={{ base: 8, md: 12 }}>
          <VStack spacing={4}>
            <Heading
              as='h2'
              size={{ base: 'lg', md: 'xl' }}
              textAlign='center'
              color='brand.light'
              letterSpacing='wider'
              px={{ base: 4, md: 0 }}>
              Our Vision in Action
            </Heading>
            <Text
              color='gray.300'
              fontSize={{ base: 'md', md: 'lg' }}
              maxW='800px'
              textAlign='center'
              px={{ base: 4, md: 0 }}>
              We're building the next generation of development tools, designed to streamline
              workflows and enhance productivity through AI-powered automation.
            </Text>
          </VStack>
          <SimpleGrid columns={{ base: 1, md: 2 }} spacing={{ base: 6, md: 8 }} width='100%'>
            <ProductCard
              title='TestSenseAI'
              description='Our AI-powered testing suite learns from your codebase to generate comprehensive test cases, helping catch bugs early and ensure robust code quality.'
              icon={TestTube2}
              status='Beta Testing'
              imageSrc='./testSenseImg.jpg'
              onLearnMore={() => handleProductClick('TestSenseAI')}
            />
            <ProductCard
              title='PullSense'
              description='Automated code reviews that provide instant, intelligent feedback on your pull requests, helping maintain code quality while saving valuable review time.'
              icon={GitPullRequest}
              status='Early Development'
              imageSrc='./pullsenseImg.jpg'
              onLearnMore={() => handleProductClick('PullSense')}
            />
          </SimpleGrid>
        </VStack>
      </Container>
    </Box>
  );
};

export default Products;
